/**
 * Retour sur une liste de véhicules (marketplace filtrée, landing, page dynamique) depuis une fiche :
 * la carte touchée revient à la même hauteur dans l'écran.
 *
 * Restaurer un nombre de pixels ne suffit pas : sur iPhone, l'en-tête et la barre glissent au défilement
 * (`mp-scroll-down`), l'état « filtre actif » replie le titre, et Safari restaure avant que les cartes et
 * leurs photos aient leur taille. La hauteur au-dessus de la grille n'est donc plus la même au départ et au
 * retour (165 px d'écart mesurés depuis 700 px sur un iPhone 17).
 *
 * - Au toucher d'une carte : on garde l'identifiant de la voiture et le haut de sa carte dans l'écran
 *   (`sessionStorage`, par onglet), avec l'adresse de la liste.
 * - Au retour par l'historique (ou rechargement) sur cette même adresse : on cale la carte à ce même haut, puis on
 *   recale pendant que la page se met en place (cartes rendues, photos chargées), jusqu'à ce que le
 *   visiteur touche l'écran, change d'adresse (Précédent, filtres), ou 2,5 s après le chargement complet. Carte disparue
 *   (vendue) : la position du navigateur reste. Le souvenir sert une fois : effacé à la fin du recalage
 *   (gardé si la page se recharge avant), ou dès que la liste est ouverte autrement que par l'historique.
 */

export const LIST_ANCHOR_KEY = "js:list-anchor";
/** Au-delà, le souvenir est trop ancien pour être le retour d'une fiche ouverte depuis la liste. */
export const LIST_ANCHOR_MAX_AGE_MS = 30 * 60 * 1000;
/** Durée pendant laquelle la position est recalée après le retour. */
export const LIST_ANCHOR_SETTLE_MS = 2500;
/** Plafond du recalage quand le chargement complet n'arrive pas. */
export const LIST_ANCHOR_MAX_SETTLE_MS = 10_000;
/** Écart toléré (px) avant de recaler : en dessous, on ne bouge pas la page. */
export const LIST_ANCHOR_TOLERANCE = 1;

const CARD = ".vehicle-card";
/** Boutons de la carte (carrousel, comparateur…) : ils n'ouvrent pas la fiche. */
const NOT_OPENING = "button, .nav-button, .vehicle-card__compare-favorite, [data-action]";
const CARD_LINK = 'a[href*="/details/"]';

export interface ListAnchor {
    /** Adresse de la liste (chemin et filtres). */
    list: string;
    /** Identifiant de la voiture (fin de l'adresse de sa fiche, `/details/<slug>-<id>`). */
    vehicleId: string;
    /** Haut de la carte dans l'écran au moment du toucher (px). */
    top: number;
    at: number;
}

/** Identifiant de la voiture d'une adresse de fiche, ou null. */
export function vehicleIdFromHref(href: string | null | undefined): string | null {
    if (!href) return null;
    let path = href;
    try {
        path = new URL(href, "http://x").pathname;
    } catch {
        return null;
    }
    const match = /^\/details\/.*-(\d+)\/?$/.exec(path);
    return match ? match[1] : null;
}

/** Identifiant de la voiture d'une carte, ou null. */
export function cardVehicleId(card: Element): string | null {
    for (const link of card.querySelectorAll<HTMLAnchorElement>(CARD_LINK)) {
        const id = vehicleIdFromHref(link.getAttribute("href"));
        if (id) return id;
    }
    return null;
}

/** Souvenir valable pour cette liste, ou null (autre liste, trop ancien, illisible). */
export function readAnchor(raw: string | null, list: string, now: number): ListAnchor | null {
    if (!raw) return null;
    try {
        const anchor = JSON.parse(raw) as ListAnchor;
        if (anchor.list !== list || typeof anchor.top !== "number" || !anchor.vehicleId) return null;
        if (now - anchor.at > LIST_ANCHOR_MAX_AGE_MS) return null;
        return anchor;
    } catch {
        return null;
    }
}

/** Défilement à appliquer pour que la carte (haut actuel `cardTop`) revienne à `anchorTop`, ou null. */
export function scrollCorrection(cardTop: number, anchorTop: number): number | null {
    const delta = cardTop - anchorTop;
    return Math.abs(delta) > LIST_ANCHOR_TOLERANCE ? delta : null;
}

function currentList(): string {
    return location.pathname + location.search;
}

function findCard(doc: Document, vehicleId: string): HTMLElement | null {
    for (const card of doc.querySelectorAll<HTMLElement>(CARD)) {
        if (cardVehicleId(card) === vehicleId) return card;
    }
    return null;
}

/**
 * Type d'arrivée sur la liste : `back_forward` (retour par l'historique), `reload` (page rechargée, par
 * exemple par le filet de secours de la recherche pendant un retour : le souvenir n'existe alors que s'il
 * n'a pas encore servi) ou `navigate` (nouvelle visite).
 */
function arrivalType(): string {
    try {
        const nav = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
        return nav?.type ?? "navigate";
    } catch {
        return "navigate";
    }
}

function rememberCard(card: HTMLElement): void {
    const vehicleId = cardVehicleId(card);
    if (!vehicleId) return;
    const anchor: ListAnchor = {
        list: currentList(),
        vehicleId,
        top: Math.round(card.getBoundingClientRect().top),
        at: Date.now(),
    };
    try {
        sessionStorage.setItem(LIST_ANCHOR_KEY, JSON.stringify(anchor));
    } catch {
        // Stockage indisponible (navigation privée) : retour sans recalage
    }
}

const STOP_EVENTS = ["touchstart", "wheel", "keydown", "pointerdown"] as const;

function forgetAnchor(): void {
    try {
        sessionStorage.removeItem(LIST_ANCHOR_KEY);
    } catch {
        // Stockage indisponible
    }
}

/** Cale la carte de `anchor`, puis la recale pendant que la page se met en place. */
function restore(anchor: ListAnchor, doc: Document): void {
    let stopped = false;
    const align = (): void => {
        if (stopped) return;
        if (currentList() !== anchor.list) {
            stop(false);
            return;
        }
        const card = findCard(doc, anchor.vehicleId);
        if (!card) return;
        const delta = scrollCorrection(card.getBoundingClientRect().top, anchor.top);
        if (delta !== null) window.scrollBy(0, delta);
    };

    // `served` : le recalage est allé à son terme (délai ou geste du visiteur), le souvenir a servi. Arrêté par
    // un changement d'adresse, il est gardé : si la page se recharge sur cette adresse (filet de secours de la
    // recherche), il resservira ; une autre adresse ne le lit pas, une nouvelle visite l'efface
    const stop = (served = true): void => {
        if (stopped) return;
        stopped = true;
        if (served) forgetAnchor();
        observer?.disconnect();
        for (const type of STOP_EVENTS) window.removeEventListener(type, onGesture, true);
        window.removeEventListener("popstate", onPopState);
    };
    // Le visiteur reprend la main (geste) ou change d'adresse (Précédent, filtre) : on ne bouge plus la page.
    // Un `popstate` sans changement d'adresse (retour depuis le cache arrière) ne compte pas
    const onGesture = (): void => stop();
    for (const type of STOP_EVENTS) window.addEventListener(type, onGesture, { capture: true, passive: true });
    const onPopState = (): void => {
        if (currentList() !== anchor.list) stop(false);
    };
    window.addEventListener("popstate", onPopState);

    // Cartes rendues ou redimensionnées (photos, rendu de la recherche) : recalage
    const observer =
        typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => requestAnimationFrame(align)) : null;
    const grid = findCard(doc, anchor.vehicleId)?.parentElement?.parentElement ?? doc.body;
    observer?.observe(grid);
    observer?.observe(doc.body);

    // Fin du recalage : 2,5 s après le chargement complet (photos, recherche : la page bouge encore juste après)
    let end = 0;
    const scheduleEnd = (): void => {
        window.clearTimeout(end);
        end = window.setTimeout(() => {
            align();
            stop();
        }, LIST_ANCHOR_SETTLE_MS);
    };

    align();
    requestAnimationFrame(() => requestAnimationFrame(align));
    for (const ms of [150, 400, 800, 1500]) window.setTimeout(align, ms);
    if (doc.readyState === "complete") {
        scheduleEnd();
    } else {
        // Le délai part du chargement complet, même lent ; plafond si `load` n'arrive jamais
        const ceiling = window.setTimeout(() => {
            align();
            stop();
        }, LIST_ANCHOR_MAX_SETTLE_MS);
        window.addEventListener(
            "load",
            () => {
                window.clearTimeout(ceiling);
                align();
                if (!stopped) scheduleEnd();
            },
            { once: true },
        );
    }
}

let initialized = false;

export function initListReturnAnchor(doc: Document = document): void {
    if (initialized || typeof window === "undefined") return;
    initialized = true;

    // Au toucher d'une carte qui ouvre sa fiche dans cet onglet (lien ou zone de la carte, pas ses boutons).
    // En fin de propagation : le clic qu'un glissé du carrousel annule (et arrête) n'est pas retenu
    doc.addEventListener("click", (event) => {
        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = event.target as Element | null;
        if (!target?.closest || target.closest(NOT_OPENING)) return;
        const card = target.closest<HTMLElement>(CARD);
        if (card) rememberCard(card);
    });

    const tryRestore = (arrival: string): void => {
        // Nouvelle visite de la liste (lien, adresse tapée) : l'ancien souvenir ne sert plus
        if (arrival === "navigate") {
            forgetAnchor();
            return;
        }
        let raw: string | null = null;
        try {
            raw = sessionStorage.getItem(LIST_ANCHOR_KEY);
        } catch {
            return;
        }
        const anchor = readAnchor(raw, currentList(), Date.now());
        if (anchor) restore(anchor, doc);
    };

    // Retour par l'historique : page rechargée, ou rendue depuis le cache arrière du navigateur
    tryRestore(arrivalType());
    window.addEventListener("pageshow", (event) => {
        if (event.persisted) tryRestore("back_forward");
    });
}
